import React, { FC, useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Paper, Stack } from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { PageTitle, Logo } from "@components/common";
import { ResetPasswordForm } from "@components/form";
import { useValidateResetToken } from "@api";
import StatusCard from "@components/main/resetPassword";
import { Typography } from "@components/base";

const ResetPassword: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [expired, setExpired] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    isError: isTokenInvalid,
    isTokenValid,
    isValidationPending,
  } = useValidateResetToken({
    token,
  });

  const handleExpired = useCallback(() => setExpired(true), []);
  const handleSuccess = useCallback(() => setSuccess(true), []);

  const showForm =
    token &&
    !(expired || isTokenInvalid) &&
    !success &&
    (isTokenValid || isValidationPending);

  return (
    <Box component="main" pt={6}>
      <PageTitle title={t("auth.resetPassword")} />

      <Container maxWidth="sm">
        <Paper>
          <Stack direction="column" p={4} rowGap={3}>
            <Stack alignItems="center" direction="column" rowGap={2}>
              <Link to="/login">
                <Logo height={80} width={80} type="green0meter-with-csob" />
              </Link>

              {!showForm && success ? (
                <StatusCard
                  icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                  color="success.main"
                  shadowColor="rgba(76,175,80,0.35)"
                  title={t("auth.passwordResetSuccess")}
                  buttonLabel={t("auth.login")}
                  onButtonClick={() => navigate("/login")}
                />
              ) : !showForm && (expired || isTokenInvalid) ? (
                <StatusCard
                  icon={<CancelOutlinedIcon fontSize="inherit" />}
                  color="error.main"
                  shadowColor="rgba(211,47,47,0.35)"
                  title={t("auth.expiredRecoveryLink")}
                  buttonLabel={t("auth.requestNewLink")}
                  onButtonClick={() => navigate("/password-recovery")}
                />
              ) : (
                <>
                  <Typography variant="title">{t("auth.resetPassword")}</Typography>

                  <Typography variant="text" px={5} textAlign="center">
                    {t("auth.newPassword")}
                  </Typography>
                </>
              )}
            </Stack>

            {showForm && (
              <ResetPasswordForm
                onExpired={handleExpired}
                onSuccess={handleSuccess}
              />
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;
